import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import WishesTab from '../components/group/WishesTab';
import MembersTab from '../components/group/MembersTab';
import EventsTab from '../components/group/EventsTab';
import PetsTab from '../components/group/PetsTab';
import ChildrenTab from '../components/group/ChildrenTab';
import { listGroupPets } from '../services/pets';
import { listGroupChildren, isCarer } from '../services/children';

function prettyType(type) {
    if (!type) return '';
    return type
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

/**
 * GroupDetail is the container for one group. It owns the data fetching and the
 * tab state, but delegates each tab's UI + actions to its own component
 * (WishesTab / MembersTab / EventsTab). Those children call back through
 * `notify` (flash messages) and the `reload*` callbacks to refresh data.
 */
export default function GroupDetail() {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [group, setGroup] = useState(null);
    const [wishes, setWishes] = useState([]);
    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [events, setEvents] = useState([]);
    const [pets, setPets] = useState([]);
    const [children, setChildren] = useState([]);
    const [myGroups, setMyGroups] = useState([]); // for the wish multi-group picker
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('wishes');
    const [focusUserId, setFocusUserId] = useState(null); // filter wishes to one person
    const [flash, setFlash] = useState(null); // { type: 'success' | 'error', text }

    // Jump from a birthday (Events tab) straight to that person's wishes.
    const viewPersonWishes = useCallback((userId) => {
        setFocusUserId(userId);
        setActiveTab('wishes');
    }, []);

    const goToDashboard = () => {
        // `stay` tells the dashboard not to auto-bounce us back into this group.
        navigate('/dashboard', { state: { stay: true } });
    };

    const notify = useCallback((type, text) => {
        setFlash({ type, text });
        if (type === 'success') setTimeout(() => setFlash(null), 3000);
    }, []);

    const reloadWishes = useCallback(async () => {
        const res = await api.get(`/groups/${groupId}/wishes`);
        setWishes(res.data);
    }, [groupId]);

    const reloadMembers = useCallback(async () => {
        const res = await api.get(`/groups/${groupId}/members`);
        setMembers(res.data);
    }, [groupId]);

    const reloadInvitations = useCallback(async () => {
        // Invitations come back on the group show payload.
        const res = await api.get(`/groups/${groupId}`);
        setInvitations((res.data.invitations || []).filter((i) => i.status === 'pending'));
    }, [groupId]);

    const reloadEvents = useCallback(async () => {
        const res = await api.get(`/groups/${groupId}/events`);
        setEvents(res.data);
    }, [groupId]);

    const reloadPets = useCallback(async () => {
        // Called bare from PetsTab after a save/delete; own the failure so a bad
        // refetch can't reject unhandled or leave the list stale without a word.
        try {
            setPets(await listGroupPets(groupId));
            setChildren(await listGroupChildren(groupId));
        } catch {
            notify('error', 'Updated, but the pet list could not refresh.');
        }
    }, [groupId, notify]);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const [groupRes, wishesRes, membersRes, eventsRes, petsRes, childrenRes, myGroupsRes, catsRes] =
                    await Promise.all([
                        api.get(`/groups/${groupId}`),
                        api.get(`/groups/${groupId}/wishes`),
                        api.get(`/groups/${groupId}/members`),
                        api.get(`/groups/${groupId}/events`),
                        listGroupPets(groupId),
                        listGroupChildren(groupId),
                        api.get('/groups'),
                        api.get('/wish-categories'),
                    ]);
                if (cancelled) return;
                setGroup(groupRes.data);
                setInvitations((groupRes.data.invitations || []).filter((i) => i.status === 'pending'));
                setWishes(wishesRes.data);
                setMembers(membersRes.data);
                setEvents(eventsRes.data);
                setPets(petsRes);
                setChildren(childrenRes);
                setMyGroups(myGroupsRes.data);
                setCategories(catsRes.data);
            } catch (err) {
                if (!cancelled) notify('error', 'Failed to load this group.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [groupId, notify]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-600">Loading...</div>
            </div>
        );
    }

    if (!group) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-600">Group not found.</div>
            </div>
        );
    }

    const tabs = [
        { key: 'wishes', label: 'Wish Lists' },
        { key: 'pets', label: 'Pets' },
        { key: 'children', label: 'Children' },
        { key: 'members', label: 'Members' },
        { key: 'events', label: 'Events' },
    ];

    // Pets in this group that the signed-in user is the caretaker of — the only
    // pets they may attribute a new wish to (the API enforces the same rule).
    const myPetsHere = pets.filter((p) => p.owner_user_id === user?.id);

    // Children in this group the signed-in user is a CARER for — the only ones they may
    // attribute a new wish to. The API enforces the same rule; this just avoids offering
    // a choice that would 422.
    const myChildrenHere = children.filter((c) => isCarer(c, user?.id));

    // Admin comes from the viewer's own membership row, the way MembersTab derives it —
    // the group payload carries no is_current_user_admin flag.
    const viewerIsAdmin = members.find((m) => m.id === user?.id)?.pivot?.role === 'admin';

    return (
        <AppLayout maxWidth="max-w-7xl">
                {/* Group title row — sits inside the Groups tab, like iOS's
                    pushed GroupDetailView under the tab bar. */}
                <div className="mb-6">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={goToDashboard}
                            className="text-gray-500 hover:text-gray-800"
                            aria-label="Back to groups"
                        >
                            ←
                        </button>
                        {group.icon && <span className="text-2xl" aria-hidden="true">{group.icon}</span>}
                        <h1 className="text-2xl font-bold text-gray-900">{group.name}</h1>
                        {group.type && (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                {prettyType(group.type)}
                            </span>
                        )}
                    </div>
                    {group.description && (
                        <p className="text-sm text-gray-500 mt-1 ml-7">{group.description}</p>
                    )}
                </div>

                {flash && (
                    <div
                        className={`mb-4 p-3 rounded-md text-sm border ${
                            flash.type === 'success'
                                ? 'bg-green-50 border-green-200 text-green-700'
                                : 'bg-red-50 border-red-200 text-red-700'
                        }`}
                    >
                        {flash.text}
                    </div>
                )}

                {/* Tab navigation */}
                <div className="mb-6 border-b border-gray-200">
                    <nav className="flex gap-8">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`pb-3 font-medium text-sm border-b-2 -mb-px ${
                                    activeTab === tab.key
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {activeTab === 'wishes' && (
                    <WishesTab
                        group={group}
                        wishes={wishes}
                        events={events}
                        user={user}
                        myGroups={myGroups}
                        categories={categories}
                        pets={myPetsHere}
                        childrenList={myChildrenHere}
                        focusUserId={focusUserId}
                        onClearFocus={() => setFocusUserId(null)}
                        onChanged={reloadWishes}
                        notify={notify}
                    />
                )}

                {activeTab === 'pets' && (
                    <PetsTab
                        group={group}
                        pets={pets}
                        user={user}
                        onChanged={reloadPets}
                        notify={notify}
                    />
                )}

                {activeTab === 'children' && (
                    <ChildrenTab
                        group={group}
                        childrenList={children}
                        user={user}
                        isAdmin={viewerIsAdmin}
                        onChanged={reloadPets}
                        notify={notify}
                    />
                )}

                {activeTab === 'members' && (
                    <MembersTab
                        group={group}
                        members={members}
                        invitations={invitations}
                        user={user}
                        onMembersChanged={reloadMembers}
                        onInvitationsChanged={reloadInvitations}
                        notify={notify}
                    />
                )}

                {activeTab === 'events' && (
                    <EventsTab
                        group={group}
                        events={events}
                        members={members}
                        pets={pets}
                        user={user}
                        onEventsChanged={reloadEvents}
                        onViewPersonWishes={viewPersonWishes}
                        notify={notify}
                    />
                )}
        </AppLayout>
    );
}
